import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { canv } from "@/canv/global";
import { frameWidget } from "@/elements/frame";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import { Group } from "@/group";
import { presentationScreenVisibility, presentationUID } from "@/interface/AppStore";
import { usePresentation } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/usePresentation";
import type { PresentationModeData } from "@/interface/RightSheet/Presentations/PresentationsNavigator/types";
import { decodeHtml } from "@/shared/lib/decodeHtml";
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "./constants";
import styles from "./FullscreenPresentationLayout.module.scss";
import { usePresentationControlsVisibility } from "./hooks/usePresentationControlsVisibility";
import { PresentationContent } from "./PresentationContent";
import { PresentationHeader } from "./PresentationHeader/PresentationHeader";
import { PresentationNavigation } from "./PresentationNavigation";
import { ReactionBar } from "./ReactionBar";
import type { TurnDirection, ZoomDirection } from "./types";

const DEFAULT_ZOOM = 100;
// временная логика получения активной презентации, в последствии надо хранить ссылку на презентацию в сторе
const getPresentation = (uid: string): PresentationModeData | null => {
    const presentation = canv.canvasController.FindByUID(uid) as PresentationWidget;
    if (!presentation) {
        return null;
    }

    const frameMap = new Map<string, frameWidget>();
    canv.canvasController.items.forEach((el) => {
        if (el instanceof Group && el.isItemWrapper() && el.children[0] instanceof frameWidget) {
            frameMap.set(el.UID, el.children[0]);
        }
    });
    const frames = [...presentation.slides]
        .sort((a, b) => a.order - b.order)
        .map((slide) => frameMap.get(slide.frameUID))
        .filter(Boolean) as frameWidget[];

    return {
        title: presentation.name,
        actor: presentation.presenterToken,
        frames: frames,
        data: presentation,
    };
};

export const FullscreenPresentationLayout = () => {
    const [isVisible, setIsVisible] = useAtom(presentationScreenVisibility);
    const [UID, setUID] = useAtom(presentationUID);
    const [reactionSrc, setReactionSrc] = useState<null | string>(null);
    const [avatar, setAvatar] = useState<null | HTMLImageElement>(null);
    const isSpeaker = canv?.isBoardModeEditOrOwner?.();

    const clearReaction = useCallback(() => {
        setReactionSrc(null);
        setAvatar(null);
    }, []);

    const chooseReaction = (imgSrc: string) => {
        if (imgSrc === reactionSrc) {
            clearReaction();
            return;
        }
        setReactionSrc(imgSrc);
        const img = new Image();
        img.src = imgSrc;
        img.onload = () => {
            setAvatar(img);
        };
        img.onerror = () => {
            setAvatar(null);
        };
    };

    // временная логика, пока у нас нет нового виджета с хранимыми фреймами
    const basePresentation = useMemo(() => {
        return getPresentation(UID);
    }, [UID]);

    const presentation = usePresentation(basePresentation);

    const [currentSlide, setCurrentSlide] = useState(0);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);

    const { isControlsVisible, showControls, onControlsMouseEnter, onControlsMouseLeave } =
        usePresentationControlsVisibility({
            enabled: isVisible,
            hideDelay: 2000,
        });

    const totalSlides = presentation?.frames.length ?? 0;

    const finishPresentation = useCallback(() => {
        const presentation = canv.canvasController.FindByUID(UID);
        if (isSpeaker) {
            if (presentation && presentation instanceof PresentationWidget) {
                presentation.stop();
            } else {
                setIsVisible(false);
                setUID("");
            }
        } else {
            setIsVisible(false);
            if (presentation instanceof PresentationWidget) {
                presentation.leave();
            }
        }
    }, [setIsVisible, UID, setUID, isSpeaker]);

    const turnSlide = useCallback(
        (direction: TurnDirection) => {
            setCurrentSlide((prev) => {
                const newIndex =
                    direction === "prev"
                        ? Math.max(0, prev - 1)
                        : Math.min(totalSlides - 1, prev + 1);

                if (presentation) {
                    presentation.data.setCurrentSlide(newIndex);
                }

                return newIndex;
            });
        },
        [totalSlides, presentation],
    );

    const changeZoom = useCallback(
        (direction: ZoomDirection) => {
            setZoom((prev) => {
                const next = direction === "in" ? prev + ZOOM_STEP : prev - ZOOM_STEP;
                if (presentation) {
                    presentation.data.setZoom(next);
                }
                return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
            });
        },
        [presentation],
    );

    const reloadPresentation = useCallback(() => {
        setCurrentSlide(0);
        setZoom(DEFAULT_ZOOM);

        if (presentation) {
            presentation.data.setZoom(DEFAULT_ZOOM);
            presentation.data.setCurrentSlide(0);
        }
    }, [presentation]);

    const currentFrame = presentation?.frames[currentSlide];

    const currentPreview = currentFrame ? presentation?.previews[currentFrame.UID] : undefined;

    useEffect(() => {
        if (currentSlide >= totalSlides) {
            setCurrentSlide(Math.max(0, totalSlides - 1));
        }
    }, [totalSlides, currentSlide]);

    useEffect(() => {
        if (!isVisible) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft" && isSpeaker) {
                e.preventDefault();
                turnSlide("prev");
            }

            if (e.key === "ArrowRight" && isSpeaker) {
                e.preventDefault();
                turnSlide("next");
            }

            if (e.code === "KeyR" && isSpeaker) {
                e.preventDefault();
                reloadPresentation();
            }

            if (e.key === "Escape") {
                e.preventDefault();
                clearReaction();
                finishPresentation();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            clearReaction();
            reloadPresentation();
        };
    }, [isVisible, turnSlide, reloadPresentation, clearReaction, isSpeaker, finishPresentation]);

    useEffect(() => {
        if (presentation?.data?.zoom !== undefined) {
            setZoom(presentation.data.zoom);
        }
        if (presentation?.data?.currentSlide !== undefined) {
            setCurrentSlide(presentation.data.currentSlide);
        }
    }, [presentation]);

    useEffect(() => {
        const updatePresentation = (e: CustomEvent) => {
            const data = e.detail as { zoom?: number; currentSlide?: number };
            if (typeof data.zoom === "number") {
                setZoom(data.zoom);
            }

            if (typeof data.currentSlide === "number") {
                setCurrentSlide(data.currentSlide);
            }
        };
        document.addEventListener(
            "updatePresentationModeMessage",
            updatePresentation as EventListener,
        );
        return () => {
            document.removeEventListener(
                "updatePresentationModeMessage",
                updatePresentation as EventListener,
            );
        };
    }, []);

    useEffect(() => {
        canv.appInterface.inputFocused = isVisible ? 1 : 0;
    }, [isVisible]);

    if (!isVisible || !presentation) {
        return null;
    }

    return (
        <div className={styles.fullscreenPresentationLayout}>
            <PresentationHeader
                isSpeaker={isSpeaker}
                name={decodeHtml(presentation?.title || "")}
                onFinish={finishPresentation}
            />

            <PresentationContent
                preview={currentPreview}
                zoom={zoom}
                avatar={avatar}
                reactionSrc={reactionSrc}
                onUserActivity={showControls}
            />

            {isControlsVisible && (
                <div onMouseEnter={onControlsMouseEnter} onMouseLeave={onControlsMouseLeave}>
                    {isSpeaker ? (
                        <PresentationNavigation
                            currentSlide={currentSlide}
                            totalSlides={totalSlides}
                            zoom={zoom}
                            onTurnSlide={turnSlide}
                            onChangeZoom={changeZoom}
                            onReload={reloadPresentation}
                        />
                    ) : (
                        <ReactionBar chooseReaction={chooseReaction} />
                    )}
                </div>
            )}
        </div>
    );
};
